import {useEffect} from 'react';
import {AlertCircleIcon, CheckCircle2Icon, LoaderCircleIcon} from 'lucide-react';
import {Button, Progress, DialogTitle} from '@tinct/web-components';
import {useGetJobById} from '@tinct/core-api';
import type {AudienceLeadsImportState} from '@tinct/core-api-models';

export type ImportingStepProps = {
  audienceName: string | null;
  jobId: string | null;
  onJobCompleted?: () => void;
  onRunInBackground?: () => void;
};

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

const parseAudienceLeadsImportState = (
  name: string | null,
  state: unknown,
): AudienceLeadsImportState | null => {
  if (name !== 'audience_leads_import') return null;
  if (!state || typeof state !== 'object') return null;
  return state as AudienceLeadsImportState;
};

export const ImportingStep = ({
  audienceName,
  jobId,
  onJobCompleted,
  onRunInBackground,
}: ImportingStepProps) => {
  const {data: job} = useGetJobById(
    {
      id: jobId ?? '',
      fields: {
        id: null,
        name: null,
        status: null,
        progress: null,
        errorMessage: null,
        state: null,
      },
    },
    {
      enabled: !!jobId,
      refetchInterval: (query) => {
        const j = query.state.data;
        if (!j?.status) return POLL_INTERVAL_MS;
        return TERMINAL_STATUSES.has(j.status) ? false : POLL_INTERVAL_MS;
      },
    },
  );

  const jobStatus = job?.status ?? null;
  const progress = job?.progress ?? null;
  const errorMessage = job?.errorMessage ?? null;
  const leadsState = parseAudienceLeadsImportState(job?.name ?? null, job?.state);

  const isFailed = jobStatus === 'failed';
  const isCompleted = jobStatus === 'completed';

  useEffect(() => {
    if (isCompleted) onJobCompleted?.();
  }, [isCompleted, onJobCompleted]);

  return (
    <div className="relative overflow-clip rounded-3xl border bg-background shadow-lg">
      {/* Header */}
      <div className="p-8">
        <DialogTitle className="text-2xl font-semibold tracking-[-0.4px] text-center">
          {audienceName ? `Creating "${audienceName}"` : 'Audience creation'}
        </DialogTitle>
      </div>

      {/* Content */}
      <div className="px-8 pb-8">
        <div className="flex flex-col items-center justify-center gap-6 rounded-lg p-6">
          <div
            className={
              'flex items-center justify-center size-10 rounded-lg ' +
              (isFailed
                ? 'bg-destructive/10 text-destructive'
                : isCompleted
                  ? 'bg-emerald-100 text-emerald-600'
                  : 'bg-muted')
            }
          >
            {isFailed ? (
              <AlertCircleIcon className="size-6" />
            ) : isCompleted ? (
              <CheckCircle2Icon className="size-6" />
            ) : (
              <LoaderCircleIcon className="size-6 animate-spin" />
            )}
          </div>
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-lg font-medium">
              {isFailed
                ? 'Import failed'
                : isCompleted
                  ? 'Finalizing your audience…'
                  : 'Importing companies…'}
            </p>
            <p className="text-sm text-muted-foreground">
              {isFailed
                ? errorMessage ?? 'Something went wrong while importing. Please try again.'
                : 'Please wait — this may take a few moments.'}
            </p>
          </div>

          {!isFailed && progress !== null && progress >= 0 && (
            <div className="w-full max-w-sm">
              <Progress value={Math.min(100, Math.max(0, progress))} />
            </div>
          )}

          {!isFailed && leadsState && (
            <div className="w-full max-w-sm grid grid-cols-2 gap-3 text-sm">
              <ProgressStat
                label="Imported"
                value={leadsState.totalProcessedLeads}
                total={leadsState.totalLeadsToImport}
              />
              <ProgressStat
                label="Enriched"
                value={leadsState.totalEnrichedLeads}
                total={leadsState.totalLeadsToEnrich}
              />
            </div>
          )}

          {!isFailed && !isCompleted && onRunInBackground && (
            <Button type="button" onClick={onRunInBackground}>
              Run in background
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

type ProgressStatProps = {
  label: string;
  value: number | null;
  total: number | null;
};

const ProgressStat = ({label, value, total}: ProgressStatProps) => (
  <div className="flex flex-col items-center gap-1 rounded-lg border bg-muted/30 p-3">
    <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="text-base font-medium tabular-nums">
      {value ?? "-"}
      <span className="text-muted-foreground"> / {total ?? "-"}</span>
    </span>
  </div>
);
